import CommonTooltip from "components/Tooltip/CommonTooltip";
import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { getRandomColor } from "utils/getRandomColor";

import avatar5 from "../../../assets/img/avatar_5.svg";
import ComponentCircleLoader from "../../../components/Loaders/ComponentCircleLoader";
import PricingPlan from "../../../components/PricingPlan";
import RightSlidingPanel from "../../../components/RightSlidingPanel/RightSlidingPanel";
import { useScrollShadow } from "../../../customHooks/useScrollShadow";
import {
  setPathName,
  timesheetActivityGroupUrls,
} from "../../../filterReducer";
import { CommonGrid, PageTitle } from "../../../styledComponents/common";
import {
  DailyTime,
  StickyFilterContainer,
  TableBodyContainer,
  TableBodyItem,
  TableContainer,
  TableHeadContainer,
  TableHeadItem,
  TimeRangeText,
  TimesheetContainer,
  TimesheetDate,
  TotalTimeContainer,
  TotalTimeDiv,
  TotalTimeLabel,
  TotalTimeText,
  WeeklyTotal,
} from "../../../styledComponents/timesheet";
import {
  formatHmsString,
  getHours,
  getHoursWithSeconds,
  toHHMMSS,
} from "../../../utils/helper";
import { TimeDisplay } from "../TimeDisplay";
import TimesheetDetails from "../TimesheetDetailsPanel";
import TimesheetFilter from "../TimesheetFilter";

const getTimes = endDate => {
  const times = [];
  let startDate = moment(endDate).subtract(13, "days");
  while (moment(startDate).isSameOrBefore(moment(endDate))) {
    times.push({
      date: startDate.clone(),
      hours: "0 h 00 m",
    });
    startDate = startDate.add(1, "days");
  }
  return times;
};

const Biweekly = props => {
  const {
    isLoading,
    selectedOrganization,
    profileData,
    clearTimesheetList,
    getOtherTimesheets,
    timesheetsList,
    getDailyTimesheets,
    dailyTimesheetLoading,
    dailyTimesheets,
    orgMembersShortList,
    getOrganizationMembersShortList,
    getOfficeDetails,
    officeDetails,
    getOfficesShortList,
    officesShortList,
    filters,
  } = props;
  const dispatch = useDispatch();
  const location = useLocation();

  const [selectedProject, setSelectedProject] = useState(
    timesheetActivityGroupUrls.includes(filters?.pathname) &&
      filters?.selectedProject
      ? filters?.selectedProject
      : { value: null, label: "(All Active Projects)" },
  );
  const [endDate, setEndDate] = useState(moment().endOf("week")); // keep as moment
  const [selectedMember, setSelectedMember] = useState(
    timesheetActivityGroupUrls.includes(filters?.pathname) &&
      filters?.selectedMember
      ? filters?.selectedMember
      : "",
  );
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(
    timesheetActivityGroupUrls.includes(filters?.pathname) &&
      filters?.selectedOffice
      ? filters?.selectedOffice
      : { value: "", label: "(All Teams)" },
  );

  const [timesheetsData, setTimesheetsData] = useState(null);
  const [detailsUserData, setDetailsUserData] = useState(null);
  const [detailsPanelIsOpen, setDetailsPanelIsOpen] = useState(false);
  const [bgColors, setBgColors] = useState({});
  const [pricingPopup, setPricingPopup] = useState(false);
  const [activeDate, setActiveDate] = useState(null);
  const [relevantDates, setRelevantDates] = useState([]);
  const [expandedAll, setExpandedAll] = useState(false);
  const [expandedTimesheets, setExpandedTimesheets] = useState([]);
  const [selectedPanelDate, setSelectedPanelDate] = useState(null);
  const showShadow = useScrollShadow(60);

  // Save filters to Redux
  useEffect(() => {
    dispatch(
      setPathName({
        pathname: location.pathname,
        // selectedProject,
        selectedMember,
        selectedTimezone,
        selectedOffice,
      }),
    );
  }, [
    // selectedProject,
    selectedMember,
    selectedTimezone,
    selectedOffice,
    dispatch,
    location.pathname,
  ]);

  // Track relevant dates for navigation
  useEffect(() => {
    if (timesheetsList?.length > 0) {
      const newDates = timesheetsList.map(item => item.date).sort();
      setRelevantDates(newDates);
    }
  }, [timesheetsList]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearTimesheetList();
    };
  }, [clearTimesheetList]);

  // Refetch when filters or org change
  useEffect(() => {
    if (!selectedOrganization?.id) return;

    if (
      selectedOrganization?.plan?.nick_name === "standard-free" ||
      selectedOrganization?.plan?.nick_name === "elite-free"
    ) {
      setPricingPopup(true);
      return;
    }

    const startDate = moment(endDate).subtract(13, "days");

    const payload = {
      organization_id: selectedOrganization.id,
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD"),
      user_id: selectedMember?.id || "",
      office_id: selectedOffice?.value || "",
    };

    if (selectedTimezone?.timezone) {
      payload.timezone = selectedTimezone.timezone;
    }

    getOtherTimesheets(payload);
  }, [
    selectedOrganization?.id,
    endDate,
    selectedMember,
    selectedOffice,
    selectedTimezone,
    getOtherTimesheets,
  ]);

  // Build timesheet data
  useEffect(() => {
    const durations = [0];
    const firstWeekDurations = [0];
    const secondWeekDurations = [0];
    const times = getTimes(endDate);

    if (timesheetsList?.length > 0) {
      timesheetsList.forEach(timesheet => {
        durations.push(timesheet.duration);
        times.forEach((item, index) => {
          if (moment(item.date).format("YYYY-MM-DD") === timesheet.date) {
            if (timesheet.duration !== 0) {
              item.hours = getHoursWithSeconds(toHHMMSS(timesheet.duration));
            }
            if (index < 7) {
              firstWeekDurations.push(timesheet.duration);
            } else {
              secondWeekDurations.push(timesheet.duration);
            }
          }
        });

        if (
          detailsUserData &&
          detailsUserData.id === timesheet.user_id &&
          detailsUserData.date &&
          moment(detailsUserData.date).format("YYYY-MM-DD") === timesheet.date
        ) {
          const newTotalTime = getHoursWithSeconds(
            toHHMMSS(timesheet.duration),
          );
          if (detailsUserData.totalTime !== newTotalTime) {
            setDetailsUserData({
              ...detailsUserData,
              totalTime: newTotalTime,
            });
          }
        }
      });

      setTimesheetsData({
        durations,
        firstWeekDurations,
        secondWeekDurations,
        timesheets: [...times],
      });
    } else {
      setTimesheetsData({
        durations,
        firstWeekDurations,
        secondWeekDurations,
        timesheets: times,
      });
    }
  }, [timesheetsList, endDate, detailsUserData]);

  // Date navigation disable states
  const disabledNext = useMemo(() => {
    if (!selectedPanelDate) return false;
    if (moment(selectedPanelDate).isSame(endDate, "day")) return true;
    return !relevantDates.some(date =>
      moment(date).isAfter(moment(selectedPanelDate)),
    );
  }, [selectedPanelDate, relevantDates, endDate]);

  const disabledPrev = useMemo(() => {
    if (!selectedPanelDate) return false;
    const startDate = moment(endDate).subtract(13, "days");
    if (moment(selectedPanelDate).isSame(startDate, "day")) return true;
    return !relevantDates.some(date =>
      moment(date).isBefore(moment(selectedPanelDate)),
    );
  }, [selectedPanelDate, relevantDates, endDate]);

  // Actions
  const selectTeamMember = e => setSelectedMember(e);

  const clearMember = () => setSelectedMember("");

  const selectDate = dateRange => {
    const newEnd = moment(dateRange.endDate).endOf("week");
    setEndDate(newEnd);
  };

  const selectPanelDate = (date, userId) => {
    setSelectedPanelDate(moment(date).format("YYYY-MM-DD"));
    const payload = {
      organization_id: selectedOrganization.id,
      date: moment(date).format("YYYY-MM-DD"),
      user_id: userId,
    };
    getDailyTimesheets(payload);
  };

  const singlePanelDateChange = (direction, userId) => {
    setSelectedPanelDate(prevDate => {
      if (!prevDate) return null;
      const currentDate = moment(prevDate).format("YYYY-MM-DD");
      let newDate;
      if (direction === "right") {
        newDate =
          relevantDates.find(date => date > currentDate) ||
          moment(prevDate).add(1, "days").format("YYYY-MM-DD");
      } else {
        newDate =
          [...relevantDates].reverse().find(date => date < currentDate) ||
          moment(prevDate).subtract(1, "days").format("YYYY-MM-DD");
      }
      selectPanelDate(moment(newDate), userId);
      return moment(newDate);
    });
  };

  const toggleDetailsPanel = value => {
    setDetailsPanelIsOpen(value);
    if (!value) setDetailsUserData(null);
  };

  const getTimesheetDetails = (date, userId) => {
    if (!selectedOrganization?.id || !date || !userId) return;
    const payload = {
      organization_id: selectedOrganization.id,
      date: date.format("YYYY-MM-DD"),
      user_id: userId,
    };
    getDailyTimesheets(payload);
    toggleDetailsPanel(true);
  };

  const handleSetUserData = (date, hours) => {
    if (!date || !hours) return;
    setDetailsUserData({
      id: selectedMember ? selectedMember.id : localStorage.getItem("user_id"),
      name: selectedMember
        ? selectedMember.name
        : profileData && profileData.id
        ? `${profileData.first_name}${
            profileData.last_name ? ` ${profileData.last_name}` : ""
          }`
        : null,
      avatar: selectedMember
        ? selectedMember.avatar || avatar5
        : profileData?.thumbnail || avatar5,
      color: selectedMember ? selectedMember.color : profileData?.color || null,
      date,
      totalTime: hours,
    });
  };

  const selectOffice = e => {
    const payload = {
      organization_id: selectedOrganization ? selectedOrganization.id : "",
    };
    if (e?.value) {
      payload.office_id = e.value;
      getOfficeDetails(payload);
    }
    setSelectedOffice(e);
  };

  useEffect(() => {
    setActiveDate(selectedPanelDate);
  }, [selectedPanelDate]);

  // Select the *current* biweekly range (last week + this week)
  const selectCurrentBiWeek = () => {
    setEndDate(moment().endOf("week"));
  };

  // Go back 2 weeks
  const selectPrevBiWeek = () => {
    setEndDate(prev => moment(prev).subtract(2, "weeks").endOf("week"));
  };

  // Go forward 2 weeks (optionally clamp to current week)
  const selectNextBiWeek = () => {
    const candidate = moment(endDate).add(2, "weeks").endOf("week");
    const thisWeekEnd = moment().endOf("week");
    setEndDate(candidate.isAfter(thisWeekEnd) ? thisWeekEnd : candidate);
  };

  return (
    <div className="content">
      <PricingPlan
        isOpen={pricingPopup}
        toggle={() => setPricingPopup(!pricingPopup)}
        popupText={"You don't have permission to this feature."}
        popupTitle={"Upgrade to paid plan to access this feature"}
        history={props.history}
        backRoute={"/user/dashboard"}
      />
      <RightSlidingPanel
        isOpen={detailsPanelIsOpen}
        closePanel={() => toggleDetailsPanel(false)}
        width="500px"
      >
        {dailyTimesheetLoading ? (
          <ComponentCircleLoader padding="35vh 0" />
        ) : (
          <TimesheetDetails
            timesheetsList={dailyTimesheets}
            userData={detailsUserData}
            fromPage="other"
            expandedAll={expandedAll}
            setExpandedAll={setExpandedAll}
            expandedTimesheets={expandedTimesheets}
            setExpandedTimesheets={setExpandedTimesheets}
            selectedPanelDate={moment(selectedPanelDate)}
            singlePanelDateChange={singlePanelDateChange}
            onPanelDateChange={selectPanelDate}
            disabledNext={disabledNext}
            disabledPrev={disabledPrev}
            setDetailsPanelIsOpen={setDetailsPanelIsOpen}
          />
        )}
      </RightSlidingPanel>

      <CommonGrid alignItem="center">
        <PageTitle>Bi-Weekly Timesheet</PageTitle>
      </CommonGrid>
      <StickyFilterContainer showShadow={showShadow}>
        <TimesheetFilter
          page="biweekly"
          startDate={moment(endDate).subtract(13, "days")}
          endDate={endDate}
          changeDateRange={selectDate}
          selectedMember={selectedMember}
          selectMember={selectTeamMember}
          clearMember={clearMember}
          getOrganizationMembersList={getOrganizationMembersShortList}
          organizationMembersList={orgMembersShortList}
          selectedOrganization={selectedOrganization}
          officeDetails={officeDetails}
          getOfficesShortList={getOfficesShortList}
          officesShortList={officesShortList}
          selectedOffice={selectedOffice}
          selectOffice={selectOffice}
          showWeekPickerFooter={false}
          showShadow={showShadow}
          selectCurrentBiWeek={selectCurrentBiWeek}
          selectPrevBiWeek={selectPrevBiWeek}
          selectNextBiWeek={selectNextBiWeek}
        />
      </StickyFilterContainer>

      <TimesheetContainer>
        {isLoading ? (
          <ComponentCircleLoader />
        ) : (
          <>
            <TotalTimeContainer>
              <TimeRangeText>
                {`${moment(endDate)
                  .subtract(13, "days")
                  .format("MMMM DD")} - ${endDate.format("MMMM DD")}`}
              </TimeRangeText>
              <TotalTimeDiv>
                <TotalTimeLabel>Total Hour</TotalTimeLabel>
                <TotalTimeText>
                  <TimeDisplay
                    marginLeft="0px"
                    timeString={
                      timesheetsData?.durations?.length > 0
                        ? getHoursWithSeconds(
                            toHHMMSS(
                              timesheetsData.durations
                                .slice(1)
                                .reduce(
                                  (prev, cur) => prev + cur,
                                  timesheetsData.durations[0],
                                ),
                            ),
                          )
                        : "-"
                    }
                  />
                </TotalTimeText>
              </TotalTimeDiv>
            </TotalTimeContainer>

            <TableContainer>
              {/* Week 1 */}
              <TableHeadContainer>
                {timesheetsData?.timesheets?.slice(0, 7).map((time, index) => (
                  <TableHeadItem key={index}>
                    {moment(time.date).format("ddd")}
                  </TableHeadItem>
                ))}
                <TableHeadItem total>Total</TableHeadItem>
              </TableHeadContainer>
              {/* First week */}
              <TableBodyContainer>
                {timesheetsData?.timesheets?.slice(0, 7).map((time, index) => {
                  if (!bgColors[time.date])
                    bgColors[time.date] = getRandomColor();
                  return (
                    <TableBodyItem key={index}>
                      {time.hours === "0 h 00 m" ? (
                        <DailyTime
                          backgroundColor="#EAF9F7"
                          isActive={
                            activeDate ===
                              moment(time.date).format("YYYY-MM-DD") &&
                            detailsPanelIsOpen
                          }
                        >
                          -
                        </DailyTime>
                      ) : (
                        <DailyTime
                          backgroundColor="#EAF9F7"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            getTimesheetDetails(
                              time.date,
                              selectedMember
                                ? selectedMember.id
                                : localStorage.getItem("user_id"),
                            );
                            handleSetUserData(time.date, time.hours);
                            setSelectedPanelDate(
                              moment(time.date).format("YYYY-MM-DD"),
                            );
                          }}
                          isActive={
                            activeDate ===
                              moment(time.date).format("YYYY-MM-DD") &&
                            detailsPanelIsOpen
                          }
                          largerZ={detailsPanelIsOpen}
                        >
                          <CommonTooltip
                            tooltipText={formatHmsString(time.hours)}
                          >
                            <TimeDisplay
                              marginLeft="0px"
                              timeString={time.hours}
                            />
                          </CommonTooltip>
                        </DailyTime>
                      )}
                      <TimesheetDate today={moment().isSame(time.date, "day")}>
                        {moment(time.date).format("MMM DD")}
                      </TimesheetDate>
                    </TableBodyItem>
                  );
                })}
                <TableBodyItem>
                  <WeeklyTotal>
                    <CommonTooltip
                      tooltipText={formatHmsString(
                        getHoursWithSeconds(
                          toHHMMSS(
                            timesheetsData?.firstWeekDurations
                              .slice(1)
                              .reduce((prev, cur) => prev + cur, 0),
                          ),
                        ),
                      )}
                    >
                      <TimeDisplay
                        marginLeft="0px"
                        timeString={getHoursWithSeconds(
                          toHHMMSS(
                            timesheetsData?.firstWeekDurations
                              .slice(1)
                              .reduce((prev, cur) => prev + cur, 0),
                          ),
                        )}
                      />
                    </CommonTooltip>
                  </WeeklyTotal>
                </TableBodyItem>
              </TableBodyContainer>

              {/* Week 2 */}
              <TableBodyContainer>
                {timesheetsData?.timesheets?.slice(7, 14).map((time, index) => {
                  if (!bgColors[time.date])
                    bgColors[time.date] = getRandomColor();
                  return (
                    <TableBodyItem key={index}>
                      {time.hours === "0 h 00 m" ? (
                        <DailyTime
                          backgroundColor="#EAF9F7"
                          isActive={
                            activeDate ===
                              moment(time.date).format("YYYY-MM-DD") &&
                            detailsPanelIsOpen
                          }
                        >
                          -
                        </DailyTime>
                      ) : (
                        <DailyTime
                          backgroundColor="#EAF9F7"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            getTimesheetDetails(
                              time.date,
                              selectedMember
                                ? selectedMember.id
                                : localStorage.getItem("user_id"),
                            );
                            handleSetUserData(time.date, time.hours);
                            setSelectedPanelDate(
                              moment(time.date).format("YYYY-MM-DD"),
                            );
                          }}
                          isActive={
                            activeDate ===
                              moment(time.date).format("YYYY-MM-DD") &&
                            detailsPanelIsOpen
                          }
                          largerZ={detailsPanelIsOpen}
                        >
                          <CommonTooltip
                            tooltipText={formatHmsString(time.hours)}
                          >
                            <TimeDisplay
                              marginLeft="0px"
                              timeString={time.hours}
                            />
                          </CommonTooltip>
                          <></>
                        </DailyTime>
                      )}
                      <TimesheetDate today={moment().isSame(time.date, "day")}>
                        {moment(time.date).format("MMM DD")}
                      </TimesheetDate>
                    </TableBodyItem>
                  );
                })}
                <TableBodyItem>
                  <WeeklyTotal>
                    <CommonTooltip
                      tooltipText={formatHmsString(
                        getHoursWithSeconds(
                          toHHMMSS(
                            timesheetsData?.secondWeekDurations
                              .slice(1)
                              .reduce((prev, cur) => prev + cur, 0),
                          ),
                        ),
                      )}
                    >
                      <TimeDisplay
                        marginLeft="0px"
                        timeString={getHoursWithSeconds(
                          toHHMMSS(
                            timesheetsData?.secondWeekDurations
                              .slice(1)
                              .reduce((prev, cur) => prev + cur, 0),
                          ),
                        )}
                      />
                    </CommonTooltip>
                  </WeeklyTotal>
                </TableBodyItem>
              </TableBodyContainer>
            </TableContainer>
          </>
        )}
      </TimesheetContainer>
    </div>
  );
};

export default Biweekly;
